<template>
  <div>
    <v-card class="mb-3">
      <v-card-title>
        <h1>Conduct of Trials</h1>
      </v-card-title>
      <v-card-text>
        <h2 class="mb-4">Details</h2>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vesselTrial.vesselName"
              label="Vessel Name"
              :readonly="true"
            ></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input
              v-model="vesselTrial.statusDesc"
              label="Trial Status"
              :readonly="true"
            ></text-input>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vesselTrial.location"
              label="Trial Location"
            ></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="vesselTrial.assignedUserId"
              :items="users"
              item-text="name"
              item-value="userId"
              label="Assignee"
              :rules="[rules.required]"
              required
              outlined
            ></v-select>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-menu
              ref="menu"
              v-model="menu"
              :close-on-content-click="true"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="vesselTrial.startDate | formatDate"
                  label="Start Date"
                  :readonly="true"
                  v-on="on"
                  clearable
                  :rules="[rules.required]"
                  required
                  outlined
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="vesselTrial.startDate"
                no-title
                @input="menu = true"
                :rules="[rules.required]"
                required
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="6">
            <v-menu
              ref="menu"
              v-model="menu2"
              :close-on-content-click="true"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="vesselTrial.dueDate | formatDate"
                  label="Due Date"
                  :readonly="true"
                  v-on="on"
                  clearable
                  :rules="[rules.required]"
                  required
                  outlined
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="vesselTrial.dueDate"
                no-title
                @input="menu2 = true"
                :rules="[rules.required]"
                required
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              :value="vesselTrial.completed | formatDate"
              label="Completed"
              :readonly="true"
            ></text-input>
          </v-col>
        </v-row>

        <h2 class="my-4">Weather</h2>
        <text-input
          v-model="vesselTrial.weatherConditions"
          label="Weather conditions"
        ></text-input>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vesselTrial.windSpeeds"
              label="Knots"
              :rules="[rules.decimal]"
            ></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input
              v-model="vesselTrial.maximumSwell"
              label="Swell"
              :rules="[rules.decimal]"
            ></text-input>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="mb-3">
      <v-card-title>
        <h1>Machinery Status / Set-up for Trials</h1>
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-select
            v-model="vesselTrial.selectedConfiguration"
            :items="configurations"
            item-text="configuration"
            item-value="configuration"
            label="Selected Configuration"
            outlined
          ></v-select>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="12">
            <v-select
              v-model="vesselTrial.trialCategory"
              :items="categories"
              item-text="value"
              item-value="key"
              label="Selected Category"
              :readonly="disableDropdown"
              outlined
            ></v-select>
          </v-col>
        </v-row>
        <div>
          <test-select-table
            ref="testSelectTable"
            v-if="showTable"
            :id="vesselTrial.vesselId"
          ></test-select-table>
        </div>
      </v-card-text>
    </v-card>
    <v-card class="mb-3">
      <v-card-title>
        <h1>Worst Case Failure Design Intent</h1>
      </v-card-title>
      <v-card-text>
        <text-input v-model="vesselTrial.wcfdi" label="WCFDI"></text-input>
      </v-card-text>
    </v-card>

    <v-card class="mb-3">
      <v-card-title>
        <h1>Trials Summary</h1>
      </v-card-title>
      <v-card-text>
        <h2 class="mb-4">Documentation</h2>
        <v-list class="mb-3" flat>
          <v-list-item-group multiple>
            <v-list-item v-for="(checkbox, index) in checkboxes" :key="index">
              <v-list-item-action>
                <v-checkbox
                  v-model="vesselTrial[checkbox.checked]"
                ></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ checkbox.label }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>

        <h2 class="my-4">Other</h2>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vesselTrial.powerGeneration"
              label="Power generation"
            ></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input
              v-model="vesselTrial.powerManagement"
              label="Power management"
            ></text-input>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vesselTrial.thrusters"
              label="Thrusters"
            ></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input
              v-model="vesselTrial.dpControl"
              label="DP control"
            ></text-input>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vesselTrial.sensors"
              label="Sensors"
            ></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input
              v-model="vesselTrial.positionReferences"
              label="Position references"
            ></text-input>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="12">
            <text-input
              v-model="vesselTrial.personnel"
              label="Personnel"
            ></text-input>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="12">
            <crew-select-table
              ref="crewSelectTable"
              v-if="showCrewTable"
              :id="this.vesselTrial.vesselId"
              :trialSelectedCrew="this.vesselTrial.vesselCrews"
            ></crew-select-table>
          </v-col>
        </v-row>
        <!-- Has to be a multiselect here for personnel-->
        <!-- <text-input label="Personnel"></text-input> -->
      </v-card-text>
    </v-card>

    <v-btn
      @click="putVesselTrial"
      color="primary"
      width="100%"
      :loading="loading"
      >Save</v-btn
    >
    <v-btn
      v-if="vesselTrial.status == draftStatus"
      class="mt-3"
      @click="startTrial"
      color="primary"
      width="100%"
      :loading="loading"
      >Begin Trial</v-btn
    >
    <snackbar
      :snackbar="showSnackbar"
      :snackbarText="snackbarText"
      @close="showSnackbar = false"
    ></snackbar>
  </div>
</template>

<script>
import TextInput from '@/components/TextInput.vue'
import Validation from '@/mixins/validation.js'
import ApiService from '../services/ApiService'
import Objects from '../services/Objects'
import TestSelectTable from '@/components/tables/VesselTrialTestSelectTable.vue'
import CrewSelectTable from '@/components/tables/VesselTrialCrewSelectTable.vue'
import Snackbar from '@/components/SnackbarMessage.vue'
import TrialStatus from '@/constants/Status.js'
import TrialCategory from '@/constants/Category.js'
import { trialStatusConstant } from '../utils/constants'

export default {
  name: 'VesselTrialDetails',
  components: {
    TextInput,
    TestSelectTable,
    CrewSelectTable,
    Snackbar
  },
  mixins: [Validation],
  props: {
    id: String
  },
  created() {
    this.getVesselTrial(), this.getCategories(), this.getUsers()
  },
  computed: {
    showTable: function() {
      return (
        this.vesselTrial.trialCategory == TrialCategory.AnnualDP.value &&
        this.vesselTrial.vesselId &&
        this.vesselTrial.status == TrialStatus.Draft.value
      )
    },
    disableDropdown: function() {
      return this.vesselTrial.status != TrialStatus.Draft.value
    },
    showCrewTable: function() {
      return this.vesselTrial.vesselId
    }
  },
  data() {
    return {
      vesselTrial: '',
      draftStatus: TrialStatus.Draft.value,
      checkboxes: [
        {
          label: 'DP watching keeping checklist',
          checked: 'dpWatchKeepingCheckList'
        },
        { label: 'DP arrival checklist', checked: 'dpArrivalCheckList' },
        {
          label: 'DP maintenance/incident reporting logbook',
          checked: 'dpReportingLogBook'
        },
        { label: 'ER DP field entry checklist', checked: 'dpFieldEntry' },
        { label: 'ER Watch hand over checklist', checked: 'dpWatchHand' },
        {
          label: 'Planned Maintenance System up to date',
          checked: 'systemUpToDate'
        }
      ],
      configurations: [],
      categories: [],
      showSnackbar: false,
      snackbarText: '',
      menu: false,
      menu2: false,
      users: [],
      loading: false
    }
  },
  methods: {
    getVesselTrial() {
      ApiService.get(Objects.VesselTrial, this.id)
        .then(response => {
          this.vesselTrial = response.data
          this.getConfigurations()

          if (this.$refs.crewSelectTable) {
            this.$refs.crewSelectTable.getCrew()
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    putVesselTrial() {
      this.loading = true
      if (this.$refs.crewSelectTable) {
        const selectedCrew = this.$refs.crewSelectTable.getSelectedCrew()
        this.vesselTrial.vesselCrews = selectedCrew
      }

      ApiService.put(Objects.VesselTrial, this.id, this.vesselTrial).then(
        () => {
          this.getVesselTrial()
        }
      )
    },
    getConfigurations() {
      ApiService.get(
        Objects.VesselSwitchboardConfig + '/byvessel',
        this.vesselTrial.vesselId
      ).then(response => {
        this.configurations = response.data
      })
    },
    getCategories() {
      ApiService.get(Objects.VesselTrial + '/categories').then(response => {
        this.categories = response.data
      })
    },
    startTrial() {
      this.loading = true
      let testIds = []

      if (this.$refs.testSelectTable) {
        testIds = this.$refs.testSelectTable.getSelectedTests()
        if (testIds.length == 0) {
          this.showSnackbar = true
          this.snackbarText = 'Please select the tests for this trial'
          return
        }
      }

      ApiService.put(
        Objects.VesselTrial + '/starttrial',
        this.id,
        testIds
      ).then(() => {
        this.vesselTrial.status = trialStatusConstant.Assigned.value
        this.putVesselTrial()
      })
    },
    getUsers() {
      ApiService.get(Objects.User).then(response => {
        this.users = response.data
      })
    }
  }
}
</script>

<style scoped></style>
